import React from 'react';
import { Box, Stack, Typography, Chip, Tooltip, Paper, alpha, useTheme } from '@mui/material';
import * as Lucide from 'lucide-react';

import {
    LIMITE_PADRAO,
    BUFFER_PADRAO,
    corEficiencia,
    statusEficiencia,
    fmtPct,
    fmtLogRemocao,
    fmtContagem,
    COR_SEM,
} from '../utils/eficiencia';

/**
 * Resumo da eficiência de remoção de E.coli (afluente → efluente).
 *
 * UM componente, dois usos:
 *  - variant="card"    → compacto, no card da listagem de Análises (2 linhas)
 *  - variant="detalhe" → destacado, no topo do modal de Detalhes
 *
 * Toda a regra de cor/conformidade vem de utils/eficiencia.js (fonte única), então card,
 * modal e o dashboard da Home nunca divergem.
 */
export default function EficienciaResumo({
    eficiencia,
    gmAfluente,
    gmEfluente,
    naoDetectado = false,
    estado, // { key, label, hint } de estadoResultado()
    invalida = false,
    motivo = null,
    limite = LIMITE_PADRAO,
    buffer = BUFFER_PADRAO,
    variant = 'card',
}) {
    const theme = useTheme();
    const detalhe = variant === 'detalhe';

    // Estado vazio (sem resultado / calculando / inválida / não calculável): uma linha
    // discreta, com o motivo no tooltip. Nunca mostra um % colorido por engano.
    const vazio = estado && estado.key !== 'ok';
    if (vazio) {
        // Duas esperas MUITO diferentes, que antes tinham o mesmo icone cinza e
        // estatico — no card ficavam indistinguiveis:
        //   em-andamento = o ensaio rodando no dispositivo (horas)
        //   calculando   = o modelo rodando (segundos)
        const iconePorEstado = {
            'em-andamento': Lucide.FlaskConical,
            calculando: Lucide.LoaderCircle,
            falho: Lucide.TriangleAlert,
            'sem-resultado': Lucide.CircleDashed,
            invalida: Lucide.TriangleAlert,
            'sem-eficiencia': Lucide.CircleSlash,
        };
        const Icone = iconePorEstado[estado.key] || Lucide.CircleDashed;
        const alerta = estado.key === 'invalida' || estado.key === 'falho';

        let corEstado = theme.palette.text.secondary;
        if (alerta) corEstado = theme.palette.error.main;
        else if (estado.key === 'em-andamento') corEstado = theme.palette.info.main;
        else if (estado.key === 'calculando') corEstado = theme.palette.primary.main;

        // O modelo leva segundos: giro continuo, que e a linguagem universal de
        // "trabalhando agora". O ensaio leva horas: um giro rapido ali sugeriria
        // pressa e viraria ruido na listagem — o pulso lento diz "vivo, mas devagar".
        const animacaoPorEstado = {
            calculando: {
                animation: 'giroCalculo 1s linear infinite',
                '@keyframes giroCalculo': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                },
            },
            'em-andamento': {
                animation: 'pulsoEnsaio 2.4s ease-in-out infinite',
                '@keyframes pulsoEnsaio': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.35 },
                },
            },
        };
        const animacao = animacaoPorEstado[estado.key];

        const corpo = (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: detalhe ? 1 : 0.5 }}>
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        lineHeight: 0,
                        ...(animacao || {}),
                        // Respeita quem desativou animacoes no sistema.
                        '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                        },
                    }}
                >
                    <Icone size={detalhe ? 18 : 16} color={corEstado} />
                </Box>
                <Typography
                    variant={detalhe ? 'body2' : 'caption'}
                    sx={{ color: corEstado, fontWeight: alerta ? 600 : 500 }}
                >
                    {estado.label}
                </Typography>
            </Stack>
        );

        const conteudoVazio = estado.hint ? (
            <Tooltip title={estado.hint} placement="top">
                <Box>{corpo}</Box>
            </Tooltip>
        ) : (
            corpo
        );

        return detalhe ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="overline" color="text.secondary">
                    Resultado
                </Typography>
                {conteudoVazio}
                {motivo && (
                    <Typography
                        variant="caption"
                        color="error.main"
                        sx={{ display: 'block', mt: 0.5 }}
                    >
                        {motivo}
                    </Typography>
                )}
            </Paper>
        ) : (
            conteudoVazio
        );
    }

    const cor = invalida ? COR_SEM : corEficiencia(eficiencia, limite, buffer);
    const st = statusEficiencia(eficiencia, limite, buffer);
    const temContagens = gmAfluente != null || gmEfluente != null;

    // Efluente abaixo do limite de deteccao: a leitura honesta e a frase, nao um
    // percentual. "Nada foi detectado" e o que o metodo mede; "100% de remocao"
    // seria afirmar ausencia absoluta, que cultivo nenhum prova. O veredito de
    // conformidade (o chip) continua valendo — a eficiencia real e >= o limite
    // por qualquer limite de deteccao plausivel.
    const valorPrincipal = naoDetectado
        ? 'não detectado'
        : fmtPct(eficiencia);

    // Log de remocao ao lado do percentual: nao satura como a porcentagem e e o
    // numero que o laudo ja imprime.
    const logRemocao = naoDetectado ? null : fmtLogRemocao(eficiencia);

    // ---------- variant "card": compacto (2 linhas) ----------
    if (!detalhe) {
        return (
            <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Lucide.Gauge size={18} color={cor} />
                    <Typography
                        variant={naoDetectado ? 'subtitle1' : 'h5'}
                        fontWeight={800}
                        sx={{ color: cor, lineHeight: 1.1 }}
                    >
                        {valorPrincipal}
                    </Typography>
                    {logRemocao && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                        >
                            {logRemocao}
                        </Typography>
                    )}
                    <Chip
                        size="small"
                        variant="outlined"
                        label={st.label}
                        sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            color: cor,
                            borderColor: alpha(cor, 0.5),
                            bgcolor: alpha(cor, 0.08),
                        }}
                    />
                </Stack>
                <Typography variant="caption" color="text.secondary" noWrap>
                    {naoDetectado
                        ? 'E.coli não detectado no efluente'
                        : temContagens
                          ? `E.coli ${fmtContagem(gmAfluente)} → ${fmtContagem(gmEfluente)} NMP`
                          : 'Remoção de E.coli'}
                    {` · limite ${limite}%`}
                </Typography>
            </Stack>
        );
    }

    // ---------- variant "detalhe": destaque no topo do modal ----------
    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: alpha(cor, 0.4) }}>
            <Typography variant="overline" color="text.secondary">
                Resultado — remoção de E.coli
            </Typography>

            <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.5 }}
            >
                <Lucide.Gauge size={26} color={cor} />
                <Typography
                    variant={naoDetectado ? 'h5' : 'h3'}
                    fontWeight={800}
                    sx={{ color: cor, lineHeight: 1 }}
                >
                    {valorPrincipal}
                </Typography>
                {logRemocao && (
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                        {logRemocao}
                    </Typography>
                )}
                <Chip
                    label={st.label}
                    variant="outlined"
                    sx={{
                        fontWeight: 700,
                        color: cor,
                        borderColor: alpha(cor, 0.5),
                        bgcolor: alpha(cor, 0.1),
                    }}
                />
                {naoDetectado && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label="E.coli abaixo do limite de detecção no efluente"
                    />
                )}
            </Stack>

            {temContagens && (
                <Stack direction="row" spacing={3} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Afluente (média geométrica)
                        </Typography>
                        <Typography variant="body2" fontWeight={700} fontFamily="monospace">
                            {fmtContagem(gmAfluente)} NMP
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Efluente (média geométrica)
                        </Typography>
                        <Typography variant="body2" fontWeight={700} fontFamily="monospace">
                            {fmtContagem(gmEfluente)} NMP
                        </Typography>
                    </Box>
                </Stack>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Limite regulatório {limite}% (FEPAM/CONSEMA 355/2017)
            </Typography>

            {invalida && motivo && (
                <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ display: 'block', mt: 1, fontWeight: 600 }}
                >
                    {motivo}
                </Typography>
            )}
        </Paper>
    );
}
